// frontend/src/components/auth/RegisterForm.js
import React, { useState } from 'react';
import { registerUser, startGoogleLogin } from '../../api/auth';
import { useNavigate } from 'react-router-dom';
import { useChat } from '../../context/ChatContext.js';
import axios from '../../api/axiosInstance';
import './LoginRegister.css';
import { debugLog } from '../../utils/logging/debugLog';

export default function RegisterForm() {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();
  const { updateUser, updateMemory } = useChat();

  const validate = (name, value) => {
    switch (name) {
      case 'first_name':
      case 'last_name':
        if (!/^[A-Za-z\s'-]+$/.test(value)) {
          return 'Only letters, spaces, apostrophes, and hyphens are allowed';
        }
        break;
      case 'email':
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Invalid email format';
        }
        break;
      case 'password':
        if (value.length < 8) {
          return 'Password must be at least 8 characters';
        }
        break;
      default:
        return null;
    }
    return null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: validate(name, value) });
  };

  const isFormValid = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validate(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    debugLog('🚀 Submitting registration form:', formData);

    try {
      const res = await registerUser(formData);
      debugLog('📥 RegisterForm received response:', res);

      // Cookie is the authority now. Hydrate from /auth/me.
      const meRes = await axios.get('/api/auth/me');
      const hydratedUser = meRes.data;

      if (!hydratedUser?.id) {
        throw new Error('Registration succeeded but user hydration failed.');
      }

      updateUser(hydratedUser);
      updateMemory('userId', hydratedUser.id);
      updateMemory('userName', hydratedUser.first_name || 'Friend');

      setMessage(`Welcome, ${hydratedUser.first_name}!`);
      navigate('/welcome', {
        state: {
          first_name: hydratedUser.first_name,
          user_id: hydratedUser.id,
          user_email: hydratedUser.email,
        },
      });
    } catch (err) {
      console.error('🚨 Register catch block triggered:', err);
      setMessage(err.message || 'Registration failed.');
    }
  };

  const handleGoogleSignup = () => {
    startGoogleLogin();
  };

  return (
    <div className="auth-container">
      <form onSubmit={handleSubmit}>
        <h2>Register</h2>

        <input
          name="first_name"
          placeholder="First Name"
          value={formData.first_name}
          onChange={handleChange}
          required
        />
        {errors.first_name && <p className="form-warning">{errors.first_name}</p>}

        <input
          name="last_name"
          placeholder="Last Name"
          value={formData.last_name}
          onChange={handleChange}
          required
        />
        {errors.last_name && <p className="form-warning">{errors.last_name}</p>}

        <input
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleChange}
          required
        />
        {errors.email && <p className="form-warning">{errors.email}</p>}

        <input
          name="password"
          type="password"
          placeholder="Password"
          value={formData.password}
          onChange={handleChange}
          required
        />
        {errors.password && <p className="form-warning">{errors.password}</p>}

        <button type="submit">Register</button>

        {message && <p>{message}</p>}

        <p>or</p>

        <button
          className="auth-google-btn"
          onClick={() =>
            (window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google`)
          }
        >
          <img
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            alt="Google"
            className="google-icon"
          />
          Continue with Google
        </button>
      </form>

      <p className="auth-links">
        Already have an account? <a href="/login">Login here</a>
      </p>
    </div>
  );
}